import React, { FC, ReactElement, useState, useRef, useEffect } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import Grid from '@mui/material/Grid';
// Components
import Text from '../text';
import Space from '../layout/space';
import AddressForm from '../form/input';
import ModalButton, { ButtonType } from './templates/modalButton';
import ModalButtonContainer from './templates/modalButtonContainer';
import SelectPointMap from '../map/selectPointMap';
import SearchAddressList from '../address/searchAddressList';
import UnderlineTextButton from '../buttons/underlineButton';
import RadioLabel from '../form/radioLabel';
// Queries
import { getLatestSearchAddressCache, invalidateSearchAddressCache } from '../../utils/query';
import { useGetAutoCompleteAddresses, useGetSearchClosestAddress } from '../../core/services/geolocation';
// Resources
import { stringsRes } from '../../resources/strings';
import { PropsFromModal } from './createModalHOC';
import { Address, AddressType, AutoCompleteAddress, Location, SearchAddress, StopType } from '../../core/apiTypes';
import { DEBOUNCE_TIME_AUTOCOMPLETE, INTERVAL_CHAR_AUTOCOMPLETE, MIN_CHAR_AUTOCOMPLETE } from '../../utils/constants';
import { useDebounce } from '../hook/useDebounce';
import useGlobalStyles from '../hook/useGlobalStyles';

interface DefineAddressProps extends PropsFromModal {
    description?: string;
    hasName?: boolean;
    hasRadius?: boolean;
    /** Offers the "this is the work address" toggle (group favorites only). */
    canSetWorkAddress?: boolean;
    /** Another favorite already holds the work slot: confirming replaces it (LT-2077, AC2). */
    isWorkAddressTaken?: boolean;
    onConfirmAction?: (data: any) => void;
    onCancelAction?: () => void;
    onDeleteAction?: () => void;
    addressData?: Address;
    radiusData?: any;
}

const DefineAddress: FC<DefineAddressProps> = (props): ReactElement => {
    const {
        onRequestClose,
        addressData,
        radiusData,
        description,
        hasName = false,
        hasRadius = false,
        canSetWorkAddress = false,
        isWorkAddressTaken = false,
        onConfirmAction,
        onCancelAction,
        onDeleteAction,
    } = props;
    const classes = useStyle();
    const globalClasses = useGlobalStyles();

    // Inputs
    const [addAddressName, setAddAddressName] = useState<string>(addressData?.name ?? '');
    const [inputAddAddress, setAddAddress] = useState<string>(addressData?.details?.full_name ?? '');
    const [radius, setRadius] = useState<string>(radiusData?.radius ? (+radiusData?.radius / 1000).toString() : '');
    const [isRadiusOnPickup, setIsRadiusOnPickup] = useState<boolean>(
        radiusData?.stop_type === StopType.PICKUP || radiusData?.stop_type === null,
    );
    const [isRadiusOnDropoff, setIsRadiusOnDropoff] = useState<boolean>(
        radiusData?.stop_type === StopType.DROP_OFF || radiusData?.stop_type === null,
    );
    const [isAddressFieldFocus, setIsAddressFieldFocus] = useState(false);
    const [isWorkAddress, setIsWorkAddress] = useState<boolean>(addressData?.type === AddressType.WORK);
    // Keeps the name typed before the work toggle was turned on, to restore it when it is turned off.
    const previousName = useRef<string>(addressData?.type === AddressType.WORK ? '' : (addressData?.name ?? ''));

    // Map
    const [showMap, setShowMap] = useState<boolean>(hasRadius);
    // Selected address
    const [addressPlaceId, setAddressPlaceId] = useState<string>(null);
    const [addressLocation, setAddressLocation] = useState<Location>(addressData?.location ?? undefined);
    const [addressComplete, setAddressComplete] = useState<Address>(addressData ?? null);

    // Fetch & Refetch
    const shouldFetch: React.MutableRefObject<boolean> = useRef(false);
    const shouldRefetch: React.MutableRefObject<boolean> = useRef(false);
    const shouldRefetchClosest: boolean = inputAddAddress?.length >= MIN_CHAR_AUTOCOMPLETE && shouldRefetch.current;
    const shouldRefetchAddress: boolean = inputAddAddress?.length >= MIN_CHAR_AUTOCOMPLETE && shouldFetch.current;
    const intervalRefetchAddress: boolean = inputAddAddress?.length % INTERVAL_CHAR_AUTOCOMPLETE === 0;

    // request address from current location
    const { refetch } = useGetSearchClosestAddress<SearchAddress>({
        enabled: !!addressPlaceId && shouldRefetchClosest,
        variables: {
            placeId: addressPlaceId,
        },
        onSuccess: (data: SearchAddress) => {
            const { name, ...rest }: SearchAddress = data;
            setAddAddress(data.details?.full_name);
            const address: Address = {
                ...rest,
                type: hasName && !hasRadius ? AddressType.FAVORITE : AddressType.ADDRESS,
            };
            console.log(address);
            setAddressComplete(address);
            setAddressLocation(address.location);
        },
        onError: () => {
            shouldFetch.current = true;
            shouldRefetch.current = true;
        },
    });

    const { refetch: searchRefetch } = useGetAutoCompleteAddresses<AutoCompleteAddress[]>({
        enabled: shouldRefetchAddress && intervalRefetchAddress,
        variables: { address: inputAddAddress },
    });

    // debounce search address
    useDebounce(
        () => {
            if (shouldRefetchAddress) {
                searchRefetch();
            }
        },
        DEBOUNCE_TIME_AUTOCOMPLETE,
        [inputAddAddress, searchRefetch],
    );

    useEffect(() => {
        if (addressPlaceId && shouldRefetchClosest) {
            refetch();
            invalidateSearchAddressCache();
        }
    }, [addressPlaceId, refetch, shouldRefetchClosest]);

    // Only the group-favorite flow may retype the address; every other caller keeps the type it built.
    const getConfirmedType = (): AddressType => {
        if (isWorkAddress) {
            return AddressType.WORK;
        }
        return hasName && !hasRadius ? AddressType.FAVORITE : AddressType.ADDRESS;
    };

    const onClickConfirm = (): void => {
        if (onConfirmAction) {
            onConfirmAction(
                !hasRadius
                    ? {
                          ...addressComplete,
                          name: addAddressName || inputAddAddress,
                          ...(canSetWorkAddress ? { type: getConfirmedType() } : {}),
                      }
                    : {
                          address: { ...addressComplete, name: addAddressName || inputAddAddress },
                          radius: +radius * 1000,
                          stop_type: getStopTypeStatus(),
                      },
            );
        }
        onRequestClose();
    };

    const onToggleWorkAddress = (): void => {
        const nextIsWorkAddress = !isWorkAddress;
        if (nextIsWorkAddress) {
            previousName.current = addAddressName;
            setAddAddressName(stringsRes.favorites.work);
        } else {
            setAddAddressName(previousName.current);
        }
        setIsWorkAddress(nextIsWorkAddress);
    };

    const onClickDelete = (): void => {
        if (onDeleteAction) {
            onDeleteAction();
            onRequestClose();
        }
    };

    const getStopTypeStatus = () => {
        if (isRadiusOnDropoff && isRadiusOnPickup) {
            return null;
        }
        if (isRadiusOnDropoff) {
            return StopType.DROP_OFF;
        }
        return StopType.PICKUP;
    };

    const onClickCancel = (): void => {
        if (onCancelAction) {
            onCancelAction();
        }
        onRequestClose();
    };

    const onOpenMap = () => {
        setShowMap((prev) => !prev);
    };

    const onSelectPosition = (name: string, address: Address): void => {
        shouldFetch.current = false;
        shouldRefetch.current = false;
        setAddAddress(name);
        setAddressLocation(address.location);
        setAddressComplete({ ...address, type: hasName && !hasRadius ? AddressType.FAVORITE : AddressType.ADDRESS });
    };

    const onSelectedAddress = (data: AutoCompleteAddress) => {
        return () => {
            setIsAddressFieldFocus(false);
            shouldFetch.current = false;
            setAddAddress(data?.details?.full_name);
            setAddressLocation(data?.location);
            setAddressPlaceId(data?.place_id);
        };
    };

    const onSelectedRecentSearch = (data: Address) => {
        return () => {
            setIsAddressFieldFocus(false);
            shouldFetch.current = false;
            setAddAddress(data.details?.full_name);
            setAddressLocation(data.location);
            setAddressComplete({ ...data });
        };
    };

    const changeFocusField = (value: boolean) => () => {
        setIsAddressFieldFocus(value);
    };

    //= ============================//
    //= ========= Changes ==========//
    //= ============================//
    const onChangeNameAddress = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target;
        setAddAddressName(value);
    };

    const onChangeAddress = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target;
        setAddAddress(value);
        setAddressComplete(null);
        setAddressLocation(null);
        setAddressPlaceId(null);
        shouldFetch.current = true;
        shouldRefetch.current = true;
    };

    const onChangeRadius = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const newValue = event.target.value
            .replaceAll(',', '.')
            .replace(/[^0-9.]/, '')
            .replace(/\.(.*)\./, '.$1');
        setRadius(newValue);
    };

    const latestSearchAddresses = getLatestSearchAddressCache(inputAddAddress);
    const showRecentAddresses = latestSearchAddresses.length > 0 && isAddressFieldFocus;

    let isDisabled = false;
    if (!addressData) {
        isDisabled =
            (addressData &&
                addressData.details?.full_name === inputAddAddress &&
                addAddressName === addressData.name) ||
            (addressData && addressData.details?.full_name !== inputAddAddress && !addressComplete) ||
            (!addressData && !addressComplete);
        if (hasName) {
            isDisabled = isDisabled || addAddressName.length === 0;
        }
        if (hasRadius) {
            isDisabled =
                isDisabled ||
                radius === '' ||
                (!isRadiusOnDropoff && !isRadiusOnPickup) ||
                (addressData &&
                    addressData.details?.full_name === inputAddAddress &&
                    addAddressName === addressData.name &&
                    getStopTypeStatus() === radiusData?.stop_type &&
                    +radius === radiusData?.radius);
        }
    } else if (hasRadius) {
        isDisabled =
            radius === '' ||
            (!isRadiusOnDropoff && !isRadiusOnPickup) ||
            (addressData &&
                addressData.details?.full_name === inputAddAddress &&
                addAddressName === addressData.name &&
                getStopTypeStatus() === radiusData?.stop_type &&
                +radius === radiusData?.radius / 1000);
    } else {
        isDisabled =
            (addressData &&
                addressData.details?.full_name === inputAddAddress &&
                addAddressName === addressData.name) ||
            (addressData && addressData.details?.full_name !== inputAddAddress && !addressComplete) ||
            (!addressData && !addressComplete);
    }

    if (hasName && addAddressName.trim().length === 0) {
        isDisabled = true;
    }

    return (
        <div className={classes.defineAddressContent}>
            <div className={globalClasses.flexCol}>
                <Text.Description16 customColor="grey" text={description} />
            </div>
            <Space value={2} />
            {canSetWorkAddress && (
                <RadioLabel
                    name="workAddress"
                    onChange={onToggleWorkAddress}
                    checked={isWorkAddress}
                    title={stringsRes.policies.places.options.setAsWorkAddress}
                    description={
                        isWorkAddressTaken && isWorkAddress
                            ? stringsRes.policies.places.options.workAddressTaken
                            : stringsRes.policies.places.options.setAsWorkAddressDesc
                    }
                />
            )}
            {hasName && (
                <AddressForm
                    onChange={onChangeNameAddress}
                    name="name"
                    autoComplete="off"
                    autoFocus={!isWorkAddress}
                    disabled={isWorkAddress}
                    onFocus={changeFocusField(false)}
                    value={addAddressName}
                    label={hasRadius ? stringsRes.policies.places.areaName : stringsRes.favorites.addNewName}
                />
            )}
            <div className={globalClasses.relative}>
                <AddressForm
                    onChange={onChangeAddress}
                    name="address"
                    autoComplete="off"
                    // autoFocus={!!currentFavorite}
                    onFocus={changeFocusField(true)}
                    onBlur={changeFocusField(false)}
                    value={inputAddAddress}
                    label={stringsRes.favorites.addNewAddress}
                />
                {showRecentAddresses && (
                    <div className={classes.recentAddresses}>
                        <SearchAddressList
                            addresses={latestSearchAddresses}
                            onClick={onSelectedAddress}
                            shouldDisplayRecentSearch={false}
                            onClickFavorite={onSelectedRecentSearch}
                        />
                    </div>
                )}
                <Space value={2} />
                {hasRadius && (
                    <>
                        <Text.ShortRegularText14 text={stringsRes.policies.places.radiusDesc} customColor="grey" />
                        <Space value={2} />
                        <AddressForm
                            onChange={onChangeRadius}
                            name={stringsRes.policies.places.options.areaRadius}
                            autoComplete="off"
                            onFocus={changeFocusField(true)}
                            onBlur={changeFocusField(false)}
                            value={radius}
                            label={stringsRes.policies.places.options.areaRadius}
                        />
                        <Space value={3} />
                        <Text.ShortRegularText14
                            text={stringsRes.policies.places.options.addressAuthorization}
                            customColor="grey"
                        />
                        <Space value={2} />
                        <Grid container>
                            <Grid item xs={6}>
                                <RadioLabel
                                    name={stringsRes.policies.places.options.pickupRadius}
                                    onChange={() => {
                                        setIsRadiusOnPickup?.((prev) => !prev);
                                    }}
                                    containerClassName={classes.filterInput}
                                    checked={isRadiusOnPickup}
                                    title={stringsRes.policies.places.options.pickupRadius}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <RadioLabel
                                    name={stringsRes.policies.places.options.dropoffRadius}
                                    onChange={() => {
                                        setIsRadiusOnDropoff?.((prev) => !prev);
                                    }}
                                    containerClassName={classes.filterInput}
                                    checked={isRadiusOnDropoff}
                                    title={stringsRes.policies.places.options.dropoffRadius}
                                />
                            </Grid>
                        </Grid>
                        <Space value={3} />
                    </>
                )}
                {showMap ? (
                    <>
                        <Text.ShortRegularText14 text={stringsRes.favorites.selectOnMap} customColor="grey" />
                        <Space value={2} />
                        <SelectPointMap onSelectPosition={onSelectPosition} center={addressLocation} radius={+radius} />
                    </>
                ) : (
                    <div className={classes.openMap}>
                        <Text.ShortRegularText14 text={stringsRes.favorites.cantLocate} customColor="grey" />
                        <UnderlineTextButton
                            id="locateOnMap"
                            title={stringsRes.favorites.locateOnMap}
                            onClick={onOpenMap}
                            customColor="lightblue"
                        />
                    </div>
                )}
                <ModalButtonContainer displayEnd>
                    {onDeleteAction && (
                        <div className={globalClasses.flex}>
                            <ModalButton type={ButtonType.ERROR} onClick={onClickDelete} />
                        </div>
                    )}
                    <ModalButton type={ButtonType.CANCEL} onClick={onClickCancel} />
                    <ModalButton
                        type={ButtonType.CONFIRM}
                        onClick={onClickConfirm}
                        label={addressData?.name ? stringsRes.common.edit : stringsRes.common.add}
                        disabled={isDisabled}
                    />
                </ModalButtonContainer>
            </div>
        </div>
    );
};

const useStyle = makeStyles((theme: Theme) =>
    createStyles({
        defineAddressContent: {
            padding: '2px',
        },
        recentAddresses: {
            boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.20)',
            borderRadius: 6,
            marginTop: -4,
        },
        openMap: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
        },
        filterInput: {
            '& .MuiTypography-root': {
                fontSize: 16,
                fontWeight: 400,
                color: theme.palette.primary.main,
            },
        },
    }),
);

export default DefineAddress;
